import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';

function toRadians(rotation = [0, 0, 0]) {
  return rotation.map((degrees) => THREE.MathUtils.degToRad(degrees));
}

function LoadedStl({ geometry, modelRotation, modelScale = 1 }) {
  const groupRef = useRef();
  const meshRef = useRef();
  const rotation = useMemo(() => toRadians(modelRotation), [modelRotation]);

  const preparedGeometry = useMemo(() => {
    const nextGeometry = geometry.clone();
    nextGeometry.computeVertexNormals();
    nextGeometry.center();
    nextGeometry.computeBoundingBox();

    const size = new THREE.Vector3();
    nextGeometry.boundingBox.getSize(size);
    const maxDimension = Math.max(size.x, size.y, size.z) || 1;
    const normalizedScale = (2.2 / maxDimension) * modelScale;
    nextGeometry.scale(normalizedScale, normalizedScale, normalizedScale);

    return nextGeometry;
  }, [geometry, modelScale]);

  useFrame((state) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y = state.clock.elapsedTime * 0.55;
    groupRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.4) * 0.15;
  });

  return (
    <group ref={groupRef}>
      <mesh ref={meshRef} geometry={preparedGeometry} rotation={rotation}>
        <meshStandardMaterial color="#d7d7d7" metalness={0.35} roughness={0.45} />
      </mesh>
    </group>
  );
}

function PlaceholderModel() {
  const meshRef = useRef();

  useFrame((state) => {
    if (!meshRef.current) return;
    meshRef.current.rotation.x = state.clock.elapsedTime * 0.35;
    meshRef.current.rotation.y = state.clock.elapsedTime * 0.7;
  });

  return (
    <mesh ref={meshRef}>
      <torusKnotGeometry args={[0.75, 0.22, 140, 18]} />
      <meshStandardMaterial color="#9a9a9a" metalness={0.4} roughness={0.38} wireframe />
    </mesh>
  );
}

function ModelScene({ geometry, modelRotation, modelScale }) {
  return (
    <>
      <ambientLight intensity={0.65} />
      <directionalLight position={[3, 4, 5]} intensity={1.2} />
      <pointLight position={[-3, -2, 4]} intensity={0.5} />
      {geometry ? (
        <LoadedStl geometry={geometry} modelRotation={modelRotation} modelScale={modelScale} />
      ) : <PlaceholderModel />}
    </>
  );
}

function resolveModelPath(modelPath) {
  if (!modelPath) return '';
  if (/^https?:\/\//i.test(modelPath)) return modelPath;

  const publicUrl = process.env.PUBLIC_URL || '';
  return `${publicUrl}${modelPath.startsWith('/') ? modelPath : `/${modelPath}`}`;
}

export default function StlModelViewer({ modelPath, modelRotation, modelScale, label }) {
  const [geometry, setGeometry] = useState(null);
  const [status, setStatus] = useState(modelPath ? 'loading' : 'placeholder');
  const resolvedModelPath = useMemo(() => resolveModelPath(modelPath), [modelPath]);

  useEffect(() => {
    if (!resolvedModelPath) {
      setGeometry(null);
      setStatus('placeholder');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');

    const loader = new STLLoader();
    loader.load(
      resolvedModelPath,
      (loadedGeometry) => {
        if (cancelled) return;
        setGeometry(loadedGeometry);
        setStatus('ready');
      },
      undefined,
      () => {
        if (cancelled) return;
        setGeometry(null);
        setStatus('error');
      }
    );

    return () => {
      cancelled = true;
    };
  }, [resolvedModelPath]);

  return (
    <div className="stl-viewer" aria-label={`${label} 3D model preview`}>
      <Canvas
        camera={{ position: [0, 0, 4.2], fov: 45 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
        style={{ background: 'transparent' }}
      >
        <ModelScene geometry={geometry} modelRotation={modelRotation} modelScale={modelScale} />
      </Canvas>
      {status !== 'ready' && (
        <div className="stl-viewer-status">
          {status === 'loading' ? 'Loading STL...' : 'Add an STL path to show your model here'}
        </div>
      )}
    </div>
  );
}
